"use client";

import React from "react";
import { Loader, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div data-section-id="129128" className="backdrop-blur-xl bg-background/60 border border-primary/20 rounded-2xl p-8 md:p-12 shadow-xl w-full max-w-3xl mx-auto text-center flex flex-col items-center justify-center space-y-4" style={{
      boxShadow: "inset 0 0 20px rgba(255,255,255,0.05)"
    }}>
        <div className="size-16 rounded-full bg-secondary/40 flex items-center justify-center mb-4">
          <Sparkles className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-primary font-serif">
          Thank You for Joining
        </h3>
        <p className="text-muted-foreground text-lg">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/20 hover:bg-primary/5 text-primary rounded-full">
          Add Another Email
        </Button>
      </div>;
  }
  return <div ref={ref} className={`backdrop-blur-md bg-background/40 border border-primary/10 rounded-2xl p-6 md:p-10 shadow-lg w-full max-w-4xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    boxShadow: "0 10px 30px -15px rgba(0,0,0,0.1), inset 0 0 20px rgba(255,255,255,0.05)"
  }}>
      <Form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-start">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem className="flex-1 w-full space-y-1">
                <FormLabel className="sr-only">Your Name</FormLabel>
                <FormControl>
                  <Input placeholder="Your Name (Optional)" className="h-14 bg-background border-primary/20 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-secondary focus-visible:border-secondary text-lg px-6 rounded-xl" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem className="flex-[1.5] w-full space-y-1">
                <FormLabel className="sr-only">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="email@example.com.au" className="h-14 bg-background border-primary/20 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-secondary focus-visible:border-secondary text-lg px-6 rounded-xl" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto h-14 px-10 text-lg font-bold tracking-wide rounded-full bg-accent hover:bg-accent/90 text-white shadow-md hover:shadow-lg transition-all duration-300 shrink-0">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Joining...
              </> : "Join Us"}
          </Button>
        </div>

        <FormField control={control} name="consent_updates" rules={{
        required: "Please agree to receive updates to continue."
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
              <FormControl>
                <input type="checkbox" className="mt-1 size-5 shrink-0 rounded-sm border border-primary/20 bg-background accent-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-colors cursor-pointer" checked={field.value} onChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm md:text-base text-muted-foreground cursor-pointer font-sans">
                  I agree to receive monthly support updates and resources
                </FormLabel>
                <FormDescription className="text-xs text-muted-foreground/60 font-sans">
                  Your privacy is important. You can unsubscribe at any time.
                </FormDescription>
                <FormMessage />
              </div>
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-xl backdrop-blur-sm">
            <p className="text-destructive text-sm font-medium text-center">
              {submitError}
            </p>
          </div>}
      </Form>
    </div>;
}
export default function StayTuned() {
  return <section id="receive-monthly-support" className="dark bg-secondary text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40 flex items-center justify-center min-h-[60vh]">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559791/img/australian_flora_pattern.png)] bg-cover bg-center opacity-10 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary via-secondary/95 to-secondary" />
      </div>

      <div className="container relative z-10 px-4 mx-auto flex flex-col items-center">
        <div className="text-center mb-12 md:mb-16 max-w-4xl mx-auto">
          <h2 className="font-serif font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-primary mb-6">
            Stay Connected
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            Join our community of parents and educators to receive monthly support, practical resources, and the latest professional insights on PDA in Australia.
          </p>
        </div>

        <div className="w-full">
          <FormProvider formKey="monthly_support_form_home" formId="monthly_support_form_home" sectionName="Receive Monthly Support" defaultValues={{
          name: "",
          email: "",
          consent_updates: false
        }} submitText="Join Us" successMessage="Thank you for joining our community. We'll be in touch soon with your first update.">
            <NewsletterForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}